import React, { useState } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  ResponsiveContainer,
  Tooltip,
  LabelList,
} from 'recharts';
import {
  Inbox,
  Check,
  ArrowDownToLine,
  Users,
  TrendingUp,
  Bell,
  ArrowUpRight,
} from 'lucide-react';
import './overview.css';
import { formatAmount, formatNumber } from '../../utils/format';

const DATE_PRESETS = [
  { id: 'month', label: 'This month' },
  { id: '7', label: 'Last 7 days' },
  { id: '30', label: 'Last 30 days' },
  { id: '90', label: 'Last 90 days' },
  { id: 'custom', label: 'Custom range' },
];

const Overview = ({
  loanCountData = {},
  dashboardData = {},
  allClinicList = [],
  selectedClinic,
  onClinicChange,
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  minDate,
  maxDate,
  errors = {},
  onApply,
  onReset,
  onApplyPreset,
  activeTab,
  onTabChange,
  chartType,
  onChartTypeChange,
  chartData = [],
  maxValue = 0,
  onReviewQueue,
}) => {
  const [datePreset, setDatePreset] = useState('month');

  const pendingCount = Number(loanCountData?.pending_count) || 0;

  const handlePresetChange = (id) => {
    setDatePreset(id);
    if (id !== 'custom') onApplyPreset(id);
  };

  const handleReset = () => {
    setDatePreset('month');
    onReset();
  };

  const stageTabs = [
    { id: 'applied', label: 'Applied', count: loanCountData?.total_applied },
    { id: 'approved', label: 'Approved', count: loanCountData?.approved_count },
    { id: 'disbursed', label: 'Disbursed', count: loanCountData?.disbursed_count },
    { id: 'pending', label: 'Incomplete', count: loanCountData?.pending_count },
    { id: 'dropped', label: 'Dropped', count: loanCountData?.expired_count },
  ];

  const activeStage = stageTabs.find((t) => t.id === activeTab) || stageTabs[2];
  const latestPoint = chartData[chartData.length - 1];
  const latestLabel = latestPoint?.month || '';

  return (
    <div className="ov-page">
      <section className="ov-card ov-filter-card">
        <div className="ov-filter-head">
          <div>
            <h3>Filter overview</h3>
            <p>Compare performance across a date range and one or more clinics.</p>
          </div>
          <button type="button" className="ov-reset-btn" onClick={handleReset}>
            Reset
          </button>
        </div>

        <div className={`ov-filter-row${datePreset === 'custom' ? ' has-custom' : ''}`}>
          <label className="ov-field">
            <span>Date range</span>
            <select
              value={datePreset}
              onChange={(e) => handlePresetChange(e.target.value)}
            >
              {DATE_PRESETS.map((preset) => (
                <option key={preset.id} value={preset.id}>
                  {preset.label}
                </option>
              ))}
            </select>
          </label>

          <label className="ov-field">
            <span>Clinics</span>
            <select
              value={selectedClinic}
              onChange={(e) => onClinicChange(e.target.value)}
            >
              <option value="">All clinics</option>
              {allClinicList.map((item, idx) => (
                <option key={idx} value={item.doctorId}>
                  {item?.clinicName ?? ''}
                </option>
              ))}
            </select>
          </label>

          {datePreset === 'custom' && (
            <>
              <label className="ov-field ov-date-field">
                <span>From</span>
                <input
                  type="date"
                  value={startDate}
                  min={minDate}
                  max={maxDate}
                  onChange={onStartDateChange}
                />
              </label>
              <label className="ov-field ov-date-field">
                <span>To</span>
                <input
                  type="date"
                  value={endDate}
                  min={minDate}
                  max={maxDate}
                  onChange={onEndDateChange}
                />
              </label>
            </>
          )}
          <button type="button" className="ov-apply-btn" onClick={onApply}>
            Apply
          </button>
        </div>

        {errors.dateRange && <p className="ov-error">{errors.dateRange}</p>}
      </section>

      <div className="ov-body">
        <div className="ov-kpi-col">
          <div className="ov-kpi-grid">
            <article className="ov-kpi-card">
              <span className="ov-kpi-icon applications">
                <Inbox size={16} />
              </span>
              <span className="ov-kpi-label">Applications</span>
              <strong className="ov-kpi-value">
                {formatNumber(loanCountData?.total_applied)}
              </strong>
              <span className="ov-kpi-hint">
                {formatAmount(loanCountData?.total_loan_amount)} applied value
              </span>
            </article>

            <article className="ov-kpi-card">
              <span className="ov-kpi-icon approval">
                <Check size={16} />
              </span>
              <span className="ov-kpi-label">Approval rate</span>
              <strong className="ov-kpi-value">
                {dashboardData?.avgApprovalRate != null && dashboardData?.avgApprovalRate !== ''
                  ? `${dashboardData.avgApprovalRate}%`
                  : '—'}
              </strong>
              <span className="ov-kpi-hint">
                CarePay avg {dashboardData?.avgApprovalRateCarePay != null && dashboardData?.avgApprovalRateCarePay !== ''
                  ? `${dashboardData.avgApprovalRateCarePay}%`
                  : '—'}
              </span>
            </article>

            <article className="ov-kpi-card">
              <span className="ov-kpi-icon disbursed">
                <ArrowDownToLine size={16} />
              </span>
              <span className="ov-kpi-label">Disbursed amount</span>
              <strong className="ov-kpi-value">
                {formatAmount(loanCountData?.disbursed_amount)}
              </strong>
              <span className="ov-kpi-hint">
                {formatNumber(loanCountData?.disbursed_count)} loans disbursed
              </span>
            </article>

            <article className="ov-kpi-card">
              <span className="ov-kpi-icon clinics">
                <Users size={16} />
              </span>
              <span className="ov-kpi-label">Active clinics</span>
              <strong className="ov-kpi-value">
                {formatNumber(allClinicList.length || 1)}
              </strong>
              <span className="ov-kpi-hint muted">
                {formatNumber(dashboardData?.leadsPerClinic)} leads per clinic
              </span>
            </article>
          </div>
        </div>

        <section className="ov-card ov-chart-card">
          <div className="ov-chart-head">
            <div>
              <h3>Performance by stage</h3>
              <p>Monthly application volume</p>
            </div>
            <span className="ov-chart-trend">
              <TrendingUp size={16} />
            </span>
          </div>

          <div className="ov-stage-tabs">
            {stageTabs.map((tab) => (
              <>
              {tab.label!=='Dropped'?
              <button
                key={tab.id}
                type="button"
                className={activeTab === tab.id ? 'active' : ''}
                onClick={() => onTabChange(tab.id)}
              >
                {tab.label}
              </button>
              :""}
              </>
            ))}
          </div>

          <p className="ov-chart-summary">
            <strong>
              {chartType === 'money'
                ? formatAmount(latestPoint?.value)
                : formatNumber(latestPoint?.value ?? activeStage.count)}
            </strong>{' '}
            {activeStage.label.toLowerCase()}
            {latestLabel ? ` in ${latestLabel}` : ''}
          </p>

          <div className="ov-chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 24, right: 8, left: -12, bottom: 0 }}>
                <XAxis
                  dataKey="month"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 12, fill: '#94a3b8' }}
                />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 12, fill: '#94a3b8' }}
                  tickFormatter={(value) => formatNumber(value)}
                  domain={[0, Math.max(4, Math.ceil(maxValue * 1.2) || 4)]}
                />
                <Tooltip
                  cursor={{ fill: 'rgba(109, 94, 252, 0.06)' }}
                  formatter={(value) => [
                    chartType === 'money' ? formatAmount(value) : formatNumber(value),
                    chartType === 'money' ? 'Amount' : 'Count',
                  ]}
                />
                <Bar dataKey="value" fill="#6d5efc" radius={[6, 6, 0, 0]} barSize={28}>
                  <LabelList
                    dataKey="value"
                    position="top"
                    fill="#334155"
                    fontSize={11}
                    fontWeight={700}
                    formatter={(value) =>
                      chartType === 'money' ? formatAmount(value) : formatNumber(value)
                    }
                  />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="ov-chart-foot">
            <span className="ov-chart-live">
              <ArrowUpRight size={14} />
              Showing {activeStage.label.toLowerCase()} {chartType === 'money' ? 'amount' : 'volume'}
            </span>
            <div className="ov-chart-toggle">
              <button
                type="button"
                className={chartType === 'count' ? 'active' : ''}
                onClick={() => onChartTypeChange('count')}
              >
                Count
              </button>
              <button
                type="button"
                className={chartType === 'money' ? 'active' : ''}
                onClick={() => onChartTypeChange('money')}
              >
                Money
              </button>
            </div>
          </div>
        </section>
      </div>

      {/* <section className="ov-card ov-alert-card">
        <span className="ov-alert-icon">
          <Bell size={18} />
        </span>
        <div className="ov-alert-copy">
          <strong>
            {formatNumber(pendingCount)} application{pendingCount === 1 ? '' : 's'} need your attention
          </strong>
          <p>Review incomplete documents and pending approvals to keep your pipeline moving.</p>
        </div>
        <button type="button" className="ov-alert-btn" onClick={onReviewQueue}>
          Review queue
          <ArrowUpRight size={16} />
        </button>
      </section> */}
    </div>
  );
};

export default Overview;
